/**
 * StatusSection.tsx
 * ステータス: 全ボタン表示、クリックで即保存（フォーム全体は保存しない）
 * 予約経路: セレクト
 */
import React, { useMemo, useCallback } from 'react';
import { SectionProps, StatusOption } from '../types';
import SectionHeader from '../shared/SectionHeader';
import InlineEditField from '../shared/InlineEditField';
import { CONTACT_MEDIA_OPTIONS } from '../constants';
import { FieldMarkWrap, FieldMarkChip, useFieldMark } from '../conflict/fieldState';
import { parseRequestAutoConfirm } from '../../../lib/requestAutoConfirm';

const STATUS_FIELDS = ['status'] as const;

interface StatusSectionProps extends SectionProps {
  reservationStatusList?: StatusOption[];
  /** ステータス即保存（useReservationStatusChange経由） */
  onStatusChange?: (newStatusId: string | number) => void;
  isSavingStatus?: boolean;
}

export default function StatusSection({
  data,
  onChange,
  disabled,
  reservationStatusList = [],
  onStatusChange,
  isSavingStatus,
}: StatusSectionProps) {
  const mediaOptions = useMemo(
    () => CONTACT_MEDIA_OPTIONS.map((o) => ({ label: o, value: o })),
    [],
  );
  const statusMark = useFieldMark(STATUS_FIELDS);
  const autoConfirm = parseRequestAutoConfirm(data.json_data);

  const handleStatusClick = useCallback(
    (statusId: string | number) => {
      if (disabled || isSavingStatus) return;
      // 同じステータスを選択した場合は何もしない
      if (String(statusId) === String(data.status)) return;
      onStatusChange?.(statusId);
    },
    [disabled, isSavingStatus, data.status, onStatusChange],
  );

  return (
    <>
      <SectionHeader title="ステータス・ブッキング" />

      {/* LP のリクエスト予約: 「対応可能なら当店で確定してよいか」のお客様の回答（json_data『対応可能なら確定』） */}
      {autoConfirm && (
        <div
          data-testid="request-auto-confirm"
          style={{
            margin: '0 0 8px', padding: '6px 10px', borderRadius: 6, fontSize: '0.82rem', lineHeight: 1.5,
            border: `1px solid ${autoConfirm === 'yes' ? '#16a34a' : '#cbd5e1'}`,
            background: autoConfirm === 'yes' ? '#f0fdf4' : '#f8fafc',
            color: autoConfirm === 'yes' ? '#14532d' : '#475569',
          }}
        >
          <div style={{ fontWeight: 700 }}>
            {autoConfirm === 'yes' ? '✅ 対応可能なら確定OK（すぐに予約確定して欲しい）' : '対応可能でも確定前にお客様へ確認（返信メール後に検討したい）'}
          </div>
          <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>
            {autoConfirm === 'yes'
              ? '在庫・時間外対応の目処が立てば、お客様に聞き直さずに確定してよい'
              : 'お客様の回答: いいえ（予約フォームの既定値）'}
          </div>
        </div>
      )}

      {/* ステータスボタン一覧 */}
      <FieldMarkWrap fields={STATUS_FIELDS} className="gc-field">
        <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>
          ステータス
          <FieldMarkChip mark={statusMark} />
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {reservationStatusList.map((status) => {
            const isActive = String(status.id) === String(data.status) || status.code === data.status;
            const bgColor = status.bg_color || '#e2e8f0';
            const textColor = status.text_color || '#475569';

            return (
              <button
                key={String(status.id)}
                type="button"
                onClick={() => handleStatusClick(status.id)}
                disabled={disabled || isSavingStatus}
                style={{
                  padding: '4px 12px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  border: isActive ? `2px solid ${textColor}` : '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: disabled || isSavingStatus ? 'default' : 'pointer',
                  backgroundColor: isActive ? bgColor : '#f8f8f8',
                  color: isActive ? textColor : '#b0b0b0',
                  opacity: isActive ? 1 : 0.6,
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                }}
              >
                {status.label || status.name}
              </button>
            );
          })}
        </div>
        {isSavingStatus && (
          <div style={{ fontSize: '0.75rem', color: '#3b82f6', marginTop: 4 }}>保存中...</div>
        )}
      </FieldMarkWrap>

      <InlineEditField
        label="予約経路"
        field="contact_media"
        value={data.contact_media}
        onChange={onChange}
        disabled={disabled}
        type="combobox"
        options={mediaOptions}
        placeholder="候補から選択、または任意入力"
      />
    </>
  );
}
